import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import debounce from "lodash/debounce";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import { Col } from "reactstrap";
import { getGeocode, getLatLng } from "use-places-autocomplete";
import CustomInput from "../../Common/CustomInput/CustomInput";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const MapComponent = ({ prop, setFieldValue, value, formik, x, y }) => {
  const positionRef = useRef(null);
  const [map, setMap] = useState(null);

  const [current, setCurrent] = useState({
    lat: x ? parseFloat(x) : 24.7136,
    lng: y ? parseFloat(y) : 46.6753,
  });

  useEffect(() => {
    if (positionRef.current) {
      positionRef.current.value = value || "";
    }
  }, [value]);

  useEffect(() => {
    if (map && window.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds(current);
      map.fitBounds(bounds);
    }
  }, [current, map]);

  useEffect(() => {
    if (x !== undefined && y !== undefined && x !== null && y !== null) {
      setCurrent({
        lat: parseFloat(x),
        lng: parseFloat(y),
      });
    }
  }, [x, y]);

  const libraries = useMemo(() => ["places"], []);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;
  const isLocalhost = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const shouldDisableMapPreview = isLocalhost || !googleMapsApiKey;

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: shouldDisableMapPreview ? "" : googleMapsApiKey || "",
    libraries,
  });

  const handleManualAddressChange = useCallback(
    (e) => {
      const nextValue = e.target.value;
      setFieldValue(prop.name, nextValue);
    },
    [setFieldValue, prop.name]
  );

  const getCode = useCallback(() => {
    const typedAddress = positionRef.current?.value || "";
    if (!typedAddress || !window.google?.maps) {
      setFieldValue(prop.name, typedAddress);
      return;
    }

    getGeocode({ address: typedAddress })
      .then((results) => {
        if (!results?.length) {
          setFieldValue(prop.name, typedAddress);
          return;
        }
        const { lat, lng } = getLatLng(results[0]);
        setFieldValue("latitude", lat);
        setFieldValue("longitude", lng);
        setFieldValue(prop.name, typedAddress);
        setCurrent({ lat, lng });
      })
      .catch(() => {
        setFieldValue(prop.name, typedAddress);
      });
  }, [setFieldValue, prop.name]);

  const debouncedGetCode = useCallback(debounce(getCode, 300), [getCode]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const onMarkerDragEnd = (e) => {
    const { lat, lng } = e.latLng.toJSON();
    setCurrent({ lat, lng });
    setFieldValue("latitude", lat);
    setFieldValue("longitude", lng);

    getGeocode({ location: { lat, lng } })
      .then((results) => {
        if (results.length > 0) {
          const address = results[0].formatted_address;
          setFieldValue(prop.name, address);
          if (positionRef.current) {
            positionRef.current.value = address;
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching address:", error);
      });
  };

  const addressInput = (
    <Col md={12} className="mb-2 p-0">
      <CustomInput
        ref={positionRef}
        type="text"
        placeholder={"Address"}
        id="address"
        name="address"
        value={formik?.values?.[prop.name] ?? value ?? ""}
        touched={formik.touched}
        errors={formik.errors}
        onChange={handleManualAddressChange}
        onBlur={(e) => {
          formik.handleBlur(e);
          if (isLoaded && !loadError) {
            getCode();
          }
        }}
      />
      <small className="text-muted d-block mt-1">
        Manual address entry always works. The map preview is optional.
      </small>
      {(shouldDisableMapPreview || loadError) && (
        <small className="text-muted d-block mt-1">
          {isLocalhost
            ? "Map preview is disabled on localhost. You can still type the address manually."
            : "Map preview is unavailable in this environment. You can still type the address manually."}
        </small>
      )}
    </Col>
  );

  if (shouldDisableMapPreview || loadError) {
    return addressInput;
  }

  return (
    <>
      {isLoaded ? (
        <>
          <Autocomplete onPlaceChanged={debouncedGetCode}>
            {addressInput}
          </Autocomplete>
          <Col md={12} className="p-0">
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={current}
              zoom={15}
              onLoad={(loadedMap) => setMap(loadedMap)}
              onUnmount={onUnmount}
              clickableIcons
            >
              <Marker
                position={current}
                draggable={true}
                onDragEnd={onMarkerDragEnd}
              />
            </GoogleMap>
          </Col>
        </>
      ) : (
        addressInput
      )}
    </>
  );
};

export default MapComponent;
